import React, { useState, useEffect, useCallback } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import API from '../utils/api';

const roleLinks = {
  admin: [
    { to:'/admin', label:'🏠 Dashboard' },
    { to:'/admin/users', label:'👥 Users' },
    { to:'/admin/plans', label:'📦 Plans' },
    { to:'/admin/subscriptions', label:'💳 Subscriptions' },
    { to:'/admin/settings', label:'⚙️ Settings' },
    { to:'/admin/credentials', label:'🔑 Credentials' },
  ],
  science_teacher: [
    { to:'/teacher', label:'🏠 Dashboard' },
    { to:'/teacher/exams', label:'📝 Exams' },
    { to:'/teacher/profile', label:'👤 Profile' },
    { to:'/teacher/branding', label:'🎨 PDF Branding' },
  ],
  arts_teacher: [
    { to:'/teacher', label:'🏠 Dashboard' },
    { to:'/teacher/exams', label:'📝 Exams' },
    { to:'/teacher/profile', label:'👤 Profile' },
    { to:'/teacher/branding', label:'🎨 PDF Branding' },
  ],
  school: [
    { to:'/teacher', label:'🏠 Dashboard' },
    { to:'/teacher/exams', label:'📝 Exams' },
    { to:'/teacher/profile', label:'👤 Profile' },
    { to:'/teacher/branding', label:'🎨 PDF Branding' },
  ],
};

export default function Navbar() {
  const { user, logout, settings, subscription } = useAuth();
  const location = useLocation();
  const [menuOpen, setMenuOpen] = useState(false);
  const [pwaPrompt, setPwaPrompt] = useState(null);
  const [showInstallGuide, setShowInstallGuide] = useState(false);
  const [pendingCount, setPendingCount] = useState({ total:0, planSpecific:0, general:0 });
  const [showBell, setShowBell] = useState(false);
  const [planSubs, setPlanSubs] = useState([]);
  const [generalSubs, setGeneralSubs] = useState([]);

  useEffect(() => {
    window.addEventListener('beforeinstallprompt', e => { e.preventDefault(); setPwaPrompt(e); });
  }, []);

  const fetchPending = useCallback(async () => {
    if (user?.role !== 'admin') return;
    try {
      const r = await API.get('/admin/subscriptions/pending-count');
      setPendingCount(r.data);
    } catch {}
  }, [user]);

  useEffect(() => {
    fetchPending();
    const interval = setInterval(fetchPending, 15000);
    return () => clearInterval(interval);
  }, [fetchPending]);

  const openBell = async () => {
    setShowBell(true);
    try {
      const r = await API.get('/admin/subscriptions');
      const pending = r.data.filter(s => s.status === 'pending');
      setPlanSubs(pending.filter(s => s.payment_method === 'plan'));
      setGeneralSubs(pending.filter(s => s.payment_method !== 'plan'));
    } catch {}
  };

  const approve = async id => {
    await API.post(`/admin/subscriptions/${id}/approve`);
    setPlanSubs(s => s.filter(x => x.id !== id));
    setGeneralSubs(s => s.filter(x => x.id !== id));
    setPendingCount(c => ({ ...c, total: Math.max(0,c.total-1) }));
    fetchPending();
  };

  const reject = async id => {
    await API.post(`/admin/subscriptions/${id}/reject`);
    setPlanSubs(s => s.filter(x => x.id !== id));
    setGeneralSubs(s => s.filter(x => x.id !== id));
    setPendingCount(c => ({ ...c, total: Math.max(0,c.total-1) }));
    fetchPending();
  };

  const handleInstall = async () => {
    if (pwaPrompt) { pwaPrompt.prompt(); const r = await pwaPrompt.userChoice; if (r.outcome==='accepted') setPwaPrompt(null); }
    else setShowInstallGuide(true);
  };

  const links = roleLinks[user?.role] || [];
  const now = new Date();
  const subActive = subscription && subscription.status==='active' && new Date(subscription.end_date) > now;
  const subExpiring = subActive && new Date(subscription.end_date) < new Date(Date.now() + 7*86400000);
  const subLabel = subActive ? (subscription.plan_name||'Active Plan') : 'Subscribe';

  const SubCard = ({ s, showWarning }) => (
    <div style={{ background:'var(--card2)', borderRadius:10, padding:14, marginBottom:10 }}>
      <div style={{ display:'flex', justifyContent:'space-between', alignItems:'flex-start', gap:10, marginBottom:10 }}>
        <div style={{ flex:1 }}>
          <div style={{ fontWeight:700, fontSize:14 }}>{s.full_name}</div>
          <div style={{ fontSize:12, color:'var(--text2)' }}>📞 {s.phone}</div>
          <div style={{ fontSize:12, color:'var(--text2)' }}>
            Plan: <strong>{s.plan_name}</strong>
            {s.plan_price && <span style={{ color:'var(--primary-light)', fontWeight:700 }}> • ₹{s.plan_price}</span>}
          </div>
          {s.payment_note && <div style={{ fontSize:11, color:'var(--text3)', marginTop:2 }}>📝 {s.payment_note}</div>}
          {showWarning && (
            <div style={{ fontSize:11, color:'#fcd34d', marginTop:4, background:'rgba(245,158,11,0.1)', padding:'4px 8px', borderRadius:6 }}>
              ⚠️ General payment — verify amount before approving!
            </div>
          )}
        </div>
        {s.payment_screenshot && (
          <a href={s.payment_screenshot} target="_blank" rel="noreferrer" title="View screenshot">
            <img src={s.payment_screenshot} alt="payment" style={{ height:60, width:60, objectFit:'cover', borderRadius:8, border:'2px solid var(--border)', cursor:'pointer' }} />
          </a>
        )}
      </div>
      <div style={{ display:'flex', gap:8 }}>
        <button onClick={() => approve(s.id)} className="btn btn-success btn-sm" style={{ flex:1 }}>✅ Approve</button>
        <button onClick={() => reject(s.id)} className="btn btn-danger btn-sm" style={{ flex:1 }}>❌ Reject</button>
      </div>
    </div>
  );

  return (
    <>
      {subExpiring && (
        <div style={{ background:'rgba(245,158,11,0.15)', borderBottom:'1px solid rgba(245,158,11,0.3)', padding:'8px 20px', display:'flex', alignItems:'center', justifyContent:'center', gap:12, fontSize:13, color:'#fcd34d', flexWrap:'wrap' }}>
          ⚠️ Subscription expires {new Date(subscription.end_date).toLocaleDateString()}
          <Link to="/teacher/subscribe" style={{ color:'#f59e0b', fontWeight:700 }}>Renew →</Link>
        </div>
      )}

      <nav style={{ background:'var(--bg2)', borderBottom:'1px solid var(--border)', padding:'0 20px', position:'sticky', top:0, zIndex:100 }}>
        <div style={{ maxWidth:1200, margin:'0 auto', display:'flex', alignItems:'center', justifyContent:'space-between', height:60 }}>
          <Link to="/dashboard" style={{ textDecoration:'none', display:'flex', alignItems:'center', gap:10 }}>
            {settings.logo_path ? <img src={settings.logo_path} alt="logo" style={{ height:36, objectFit:'contain' }} /> : <span style={{ fontSize:24 }}>📝</span>}
            <span style={{ fontWeight:800, fontSize:20, color:'var(--primary-light)' }}>{settings.brand_name||'Oexams'}</span>
          </Link>

          <div style={{ display:'flex', alignItems:'center', gap:4 }} className="desktop-nav">
            {links.map(l => (
              <Link key={l.to} to={l.to} style={{ padding:'6px 14px', borderRadius:8, textDecoration:'none', fontSize:13, fontWeight:600, color:location.pathname===l.to?'white':'var(--text2)', background:location.pathname===l.to?'var(--primary)':'transparent', transition:'all 0.2s' }}>{l.label}</Link>
            ))}
          </div>

          <div style={{ display:'flex', alignItems:'center', gap:8 }}>
            {user?.role === 'admin' && (
              <button onClick={openBell} style={{ position:'relative', background:'var(--card2)', border:'1px solid var(--border)', color:'var(--text)', padding:'8px 12px', borderRadius:8, cursor:'pointer', fontSize:18, display:'flex', alignItems:'center', gap:6 }}>
                🔔
                {pendingCount.total > 0 && (
                  <span style={{ position:'absolute', top:-6, right:-6, background:'var(--danger)', color:'white', borderRadius:'50%', width:20, height:20, fontSize:11, fontWeight:800, display:'flex', alignItems:'center', justifyContent:'center' }}>{pendingCount.total}</span>
                )}
              </button>
            )}

            {user?.role !== 'admin' && (
              <Link to="/teacher/subscribe" className="desktop-only" style={{ padding:'6px 14px', borderRadius:8, background:subActive?'rgba(16,185,129,0.15)':'rgba(99,102,241,0.2)', color:subActive?'#34d399':'var(--primary-light)', textDecoration:'none', fontSize:12, fontWeight:700, border:`1px solid ${subActive?'rgba(16,185,129,0.3)':'rgba(99,102,241,0.3)'}`, whiteSpace:'nowrap' }}>
                💳 {subLabel}
              </Link>
            )}
            <button onClick={handleInstall} className="btn btn-secondary btn-sm desktop-only">📲 Install</button>
            <button onClick={logout} className="btn btn-secondary btn-sm desktop-only">🚪 Logout</button>
            <button onClick={() => setMenuOpen(!menuOpen)} className="menu-btn" style={{ background:'var(--card2)', border:'1px solid var(--border)', color:'var(--text)', padding:'8px 12px', borderRadius:8, cursor:'pointer', fontSize:20, lineHeight:1 }}>☰</button>
          </div>
        </div>
      </nav>

      {menuOpen && (
        <div style={{ background:'var(--bg2)', borderBottom:'1px solid var(--border)', padding:'12px 20px', zIndex:99 }}>
          {links.map(l => (
            <Link key={l.to} to={l.to} onClick={() => setMenuOpen(false)} style={{ display:'block', padding:'10px 14px', color:'var(--text)', textDecoration:'none', borderRadius:8, marginBottom:4, background:location.pathname===l.to?'var(--primary)':'transparent' }}>{l.label}</Link>
          ))}
          {user?.role !== 'admin' && <Link to="/teacher/subscribe" onClick={() => setMenuOpen(false)} style={{ display:'block', padding:'10px 14px', color:'var(--primary-light)', textDecoration:'none', borderRadius:8, marginBottom:4 }}>💳 {subLabel}</Link>}
          <button onClick={() => { handleInstall(); setMenuOpen(false); }} style={{ display:'block', width:'100%', textAlign:'left', padding:'10px 14px', color:'var(--text)', background:'transparent', border:'none', borderRadius:8, cursor:'pointer', fontSize:14, marginBottom:4 }}>📲 Install App</button>
          <button onClick={() => { logout(); setMenuOpen(false); }} style={{ display:'block', width:'100%', textAlign:'left', padding:'10px 14px', color:'var(--danger)', background:'transparent', border:'none', borderRadius:8, cursor:'pointer', fontSize:14 }}>🚪 Logout</button>
        </div>
      )}

      {/* Bell Panel */}
      {showBell && (
        <div className="modal-overlay" onClick={() => setShowBell(false)}>
          <div className="modal" onClick={e => e.stopPropagation()} style={{ maxWidth:580, maxHeight:'85vh', overflow:'auto' }}>
            <div className="flex-between" style={{ marginBottom:20 }}>
              <h2>🔔 Pending Payments ({pendingCount.total})</h2>
              <button onClick={() => setShowBell(false)} style={{ background:'none', border:'none', color:'var(--text2)', fontSize:22, cursor:'pointer' }}>✕</button>
            </div>

            {pendingCount.total === 0 ? (
              <div style={{ textAlign:'center', padding:'32px 0', color:'var(--text2)' }}>
                <div style={{ fontSize:48, marginBottom:8 }}>✅</div>
                <p>No pending payments!</p>
              </div>
            ) : (
              <>
                {/* Section 1 — Plan specific */}
                {planSubs.length > 0 && (
                  <div style={{ marginBottom:20 }}>
                    <div style={{ display:'flex', alignItems:'center', gap:8, marginBottom:12, padding:'8px 12px', background:'rgba(16,185,129,0.1)', borderRadius:8, border:'1px solid rgba(16,185,129,0.3)' }}>
                      <span style={{ fontSize:16 }}>💳</span>
                      <div>
                        <div style={{ fontWeight:700, color:'#34d399', fontSize:14 }}>Plan-Specific Payments ({planSubs.length})</div>
                        <div style={{ fontSize:11, color:'var(--text2)' }}>Amount is verified — safe to approve directly</div>
                      </div>
                    </div>
                    {planSubs.map(s => <SubCard key={s.id} s={s} showWarning={false} />)}
                  </div>
                )}

                {/* Section 2 — General */}
                {generalSubs.length > 0 && (
                  <div>
                    <div style={{ display:'flex', alignItems:'center', gap:8, marginBottom:12, padding:'8px 12px', background:'rgba(245,158,11,0.1)', borderRadius:8, border:'1px solid rgba(245,158,11,0.3)' }}>
                      <span style={{ fontSize:16 }}>⚠️</span>
                      <div>
                        <div style={{ fontWeight:700, color:'#fcd34d', fontSize:14 }}>General Payments ({generalSubs.length})</div>
                        <div style={{ fontSize:11, color:'var(--text2)' }}>Verify screenshot amount before approving!</div>
                      </div>
                    </div>
                    {generalSubs.map(s => <SubCard key={s.id} s={s} showWarning={true} />)}
                  </div>
                )}
              </>
            )}
            <Link to="/admin/subscriptions" onClick={() => setShowBell(false)} className="btn btn-secondary btn-full" style={{ marginTop:12 }}>View All Subscriptions →</Link>
          </div>
        </div>
      )}

      {showInstallGuide && (
        <div className="modal-overlay">
          <div className="modal">
            <h2>📲 Install as App</h2>
            <div style={{ display:'flex', flexDirection:'column', gap:12, marginBottom:20 }}>
              <div className="card card-sm"><div style={{ fontWeight:700, marginBottom:4 }}>🖥️ Chrome Desktop</div><div style={{ fontSize:13, color:'var(--text2)' }}>Click install icon (➕) in address bar</div></div>
              <div className="card card-sm"><div style={{ fontWeight:700, marginBottom:4 }}>📱 Android Chrome</div><div style={{ fontSize:13, color:'var(--text2)' }}>Tap 3-dot menu → Add to Home Screen</div></div>
              <div className="card card-sm"><div style={{ fontWeight:700, marginBottom:4 }}>🍎 iPhone Safari</div><div style={{ fontSize:13, color:'var(--text2)' }}>Tap Share → Add to Home Screen</div></div>
            </div>
            <button className="btn btn-primary btn-full" onClick={() => setShowInstallGuide(false)}>Got it!</button>
          </div>
        </div>
      )}

      <style>{`@media(max-width:768px){.desktop-nav{display:none!important}.desktop-only{display:none!important}.menu-btn{display:block!important}}@media(min-width:769px){.menu-btn{display:none!important}}`}</style>
    </>
  );
}
